import React, { useState, useEffect } from 'react';

const CATEGORIES = [
  { id: 'length', name: '길이' },
  { id: 'area', name: '면적' },
  { id: 'volume', name: '부피' },
  { id: 'weight', name: '무게' },
  { id: 'temperature', name: '온도' },
  { id: 'time', name: '시간' },
  { id: 'speed', name: '속도' },
  { id: 'pressure', name: '압력' },
];

const UNITS = {
  length: [
    { id: 'mm', name: '밀리미터 (mm)', factor: 0.001 },
    { id: 'cm', name: '센티미터 (cm)', factor: 0.01 },
    { id: 'm', name: '미터 (m)', factor: 1 },
    { id: 'km', name: '킬로미터 (km)', factor: 1000 },
    { id: 'in', name: '인치 (in)', factor: 0.0254 },
    { id: 'ft', name: '피트 (ft)', factor: 0.3048 },
    { id: 'yd', name: '야드 (yd)', factor: 0.9144 },
    { id: 'mi', name: '마일 (mi)', factor: 1609.344 },
  ],
  area: [
    { id: 'mm2', name: '제곱밀리미터 (mm²)', factor: 0.000001 },
    { id: 'cm2', name: '제곱센티미터 (cm²)', factor: 0.0001 },
    { id: 'm2', name: '제곱미터 (m²)', factor: 1 },
    { id: 'km2', name: '제곱킬로미터 (km²)', factor: 1000000 },
    { id: 'ha', name: '헥타르 (ha)', factor: 10000 },
    { id: 'in2', name: '제곱인치 (in²)', factor: 0.00064516 },
    { id: 'ft2', name: '제곱피트 (ft²)', factor: 0.092903 },
    { id: 'yd2', name: '제곱야드 (yd²)', factor: 0.836127 },
    { id: 'ac', name: '에이커 (ac)', factor: 4046.856 },
    { id: 'mi2', name: '제곱마일 (mi²)', factor: 2589988.11 },
    { id: 'pyeong', name: '평', factor: 3.305785 },
  ],
  volume: [
    { id: 'ml', name: '밀리리터 (ml)', factor: 0.001 },
    { id: 'l', name: '리터 (l)', factor: 1 },
    { id: 'm3', name: '세제곱미터 (m³)', factor: 1000 },
    { id: 'in3', name: '세제곱인치 (in³)', factor: 0.016387 },
    { id: 'ft3', name: '세제곱피트 (ft³)', factor: 28.317 },
    { id: 'us_oz', name: '미국 액량 온스 (fl oz)', factor: 0.0295735 },
    { id: 'us_cup', name: '미국 컵 (cup)', factor: 0.236588 },
    { id: 'us_pt', name: '미국 파인트 (pt)', factor: 0.473176 },
    { id: 'us_qt', name: '미국 쿼트 (qt)', factor: 0.946353 },
    { id: 'us_gal', name: '미국 갤런 (gal)', factor: 3.78541 },
  ],
  weight: [
    { id: 'mg', name: '밀리그램 (mg)', factor: 0.001 },
    { id: 'g', name: '그램 (g)', factor: 1 },
    { id: 'kg', name: '킬로그램 (kg)', factor: 1000 },
    { id: 't', name: '톤 (t)', factor: 1000000 },
    { id: 'oz', name: '온스 (oz)', factor: 28.3495 },
    { id: 'lb', name: '파운드 (lb)', factor: 453.592 },
    { id: 'stone', name: '스톤 (stone)', factor: 6350.29 },
    { id: 'don', name: '돈', factor: 3.75 },
    { id: 'geun', name: '근', factor: 600 },
  ],
  temperature: [
    { id: 'c', name: '섭씨 (°C)', convert: (value, to) => {
      if (to === 'f') return (value * 9/5) + 32;
      if (to === 'k') return value + 273.15;
      return value;
    }},
    { id: 'f', name: '화씨 (°F)', convert: (value, to) => {
      if (to === 'c') return (value - 32) * 5/9;
      if (to === 'k') return (value - 32) * 5/9 + 273.15;
      return value;
    }},
    { id: 'k', name: '켈빈 (K)', convert: (value, to) => {
      if (to === 'c') return value - 273.15;
      if (to === 'f') return (value - 273.15) * 9/5 + 32;
      return value;
    }},
  ],
  time: [
    { id: 'ms', name: '밀리초 (ms)', factor: 0.001 },
    { id: 's', name: '초 (s)', factor: 1 },
    { id: 'min', name: '분 (min)', factor: 60 },
    { id: 'h', name: '시간 (h)', factor: 3600 },
    { id: 'd', name: '일 (day)', factor: 86400 },
    { id: 'wk', name: '주 (week)', factor: 604800 },
    { id: 'mo', name: '월 (30일)', factor: 2592000 },
    { id: 'yr', name: '년 (365일)', factor: 31536000 },
  ],
  speed: [
    { id: 'mps', name: '미터/초 (m/s)', factor: 1 },
    { id: 'kph', name: '킬로미터/시 (km/h)', factor: 0.277778 },
    { id: 'mph', name: '마일/시 (mph)', factor: 0.44704 },
    { id: 'fps', name: '피트/초 (ft/s)', factor: 0.3048 },
    { id: 'knot', name: '노트 (knot)', factor: 0.514444 },
  ],
  pressure: [
    { id: 'pa', name: '파스칼 (Pa)', factor: 1 },
    { id: 'kpa', name: '킬로파스칼 (kPa)', factor: 1000 },
    { id: 'mpa', name: '메가파스칼 (MPa)', factor: 1000000 },
    { id: 'bar', name: '바 (bar)', factor: 100000 },
    { id: 'atm', name: '기압 (atm)', factor: 101325 },
    { id: 'mmhg', name: '수은주 밀리미터 (mmHg)', factor: 133.322 },
    { id: 'psi', name: '제곱인치당 파운드 (psi)', factor: 6894.76 },
  ],
};

export default function UnitConverter() {
  const [category, setCategory] = useState('length');
  const [fromUnit, setFromUnit] = useState('m');
  const [toUnit, setToUnit] = useState('cm');
  const [fromValue, setFromValue] = useState('');
  const [toValue, setToValue] = useState('');
  const [history, setHistory] = useState([]);

  // 단위 변환 함수
  const convert = () => {
    if (!fromValue || isNaN(fromValue)) {
      setToValue('');
      return;
    }

    const value = parseFloat(fromValue);
    let result;

    // 온도는 특별한 변환 로직 사용
    if (category === 'temperature') {
      const fromUnitObj = UNITS[category].find(u => u.id === fromUnit);
      result = fromUnitObj.convert(value, toUnit);
    } else {
      // 다른 단위는 기준 단위로 변환 후 다시 변환
      const fromUnitObj = UNITS[category].find(u => u.id === fromUnit);
      const toUnitObj = UNITS[category].find(u => u.id === toUnit);
      
      // 기준 단위로 변환
      const standardValue = value * fromUnitObj.factor;
      
      // 목표 단위로 변환
      result = standardValue / toUnitObj.factor;
    }

    // 소수점 처리 (너무 작거나 큰 숫자는 지수 표기법 사용)
    const formattedResult = Math.abs(result) < 0.000001 || Math.abs(result) > 999999 
      ? result.toExponential(6) 
      : result.toFixed(6).replace(/\.?0+$/, '');
    
    setToValue(formattedResult);

    // 변환 기록 저장
    if (value && !isNaN(value)) {
      const newHistoryItem = {
        category,
        fromUnit,
        toUnit,
        fromValue: value,
        toValue: formattedResult,
        timestamp: new Date().toISOString()
      };
      
      setHistory(prev => [newHistoryItem, ...prev.slice(0, 9)]);
    }
  };

  // 단위 변경 시 자동 변환
  useEffect(() => {
    convert();
  }, [category, fromUnit, toUnit, fromValue]);

  // 카테고리 변경 시 기본 단위 설정
  useEffect(() => {
    if (UNITS[category] && UNITS[category].length > 0) {
      setFromUnit(UNITS[category][0].id);
      setToUnit(UNITS[category][1]?.id || UNITS[category][0].id);
    }
  }, [category]);

  // 단위 표시 이름 가져오기
  const getUnitDisplay = (unitId, categoryId) => {
    const unit = UNITS[categoryId]?.find(u => u.id === unitId);
    return unit ? unit.name.split(' ')[0] : unitId;
  };

  return (
    <div className="max-w-3xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">단위 변환기</h1>
      
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          변환 카테고리
        </label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        >
          {CATEGORIES.map(cat => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </select>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              변환할 단위
            </label>
            <select
              value={fromUnit}
              onChange={(e) => setFromUnit(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              {UNITS[category].map(unit => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              입력값
            </label>
            <input
              type="number"
              value={fromValue}
              onChange={(e) => setFromValue(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="값을 입력하세요"
            />
          </div>
        </div>
        
        <div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              변환될 단위
            </label>
            <select
              value={toUnit}
              onChange={(e) => setToUnit(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              {UNITS[category].map(unit => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              변환 결과
            </label>
            <input
              type="text"
              value={toValue}
              readOnly
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50"
            />
          </div>
        </div>
      </div>
      
      {history.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-medium mb-4">최근 변환 기록</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    카테고리
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    변환
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    결과
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {history.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {CATEGORIES.find(cat => cat.id === item.category)?.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.fromValue} {getUnitDisplay(item.fromUnit, item.category)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.toValue} {getUnitDisplay(item.toUnit, item.category)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
} 